import { useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';

import * as S from './Form.styles';
import { ConnectionStatus } from './components/ConnectionStatus';
import { Input } from './types';

type FormProps = {
  onSubmit: () => void;
  inputs: Input[];
  buttonLabel: string;
  error?: string | Error;
  messageTitle?: string;
  register: UseFormRegister<FieldValues>;
  type?: 'login' | 'newPassword' | 'forgotPassword';
  hasForgotPassword?: boolean;
  hasBackButton?: boolean;
  hasNoAutoComplete?: boolean;
  isButtonLoading?: boolean;
};

export function Form(props: FormProps): React.ReactElement {
  const {
    onSubmit,
    buttonLabel,
    messageTitle,
    inputs,
    register,
    error = undefined,
    type = 'newPassword',
    hasForgotPassword = false,
    hasBackButton = false,
    hasNoAutoComplete = false,
    isButtonLoading = false,
  } = props;

  const [isVisible, isVisibleSet] = useState(false);
  const navigate = useNavigate();

  const inputsMapped = inputs?.map((input: Input) => (
    <S.LoginInput
      autoComplete={hasNoAutoComplete ? 'new-password' : undefined}
      key={input.name}
      data-testid={input.name}
      name={input.name}
      label={input.label}
      options={{ required: true }}
      register={register}
      type={input.type}
      motive="login"
      hasShowIcon={input.type === 'password'}
    />
  ));

  const setTitle = () => {
    switch (type) {
      case 'login':
        return 'Welcome back';
      case 'forgotPassword':
        return 'Reset password';
      default:
        return 'Complete New Password Challenge';
    }
  };

  return (
    <S.Container>
      {hasBackButton ? (
        <Button
          iconLeft="FiChevronLeft"
          icoarea="2"
          onClick={() => navigate(-1)}
          motive="backSecondary"
          scale="s"
        >
          Back
        </Button>
      ) : null}

      <S.Title
        textAlign="center"
        color="black"
        fontWeight="extraBold"
        tag="h2"
        scale={['xl', 'lg']}
      >
        {setTitle()}
      </S.Title>

      {error || messageTitle ? (
        <S.MessageContainer>
          {messageTitle && (
            <Headline
              scale="xs"
              color="borderColorDarkSecondary"
              textAlign="center"
              fontFamily="LFT Zeno"
              isWrapped
            >
              {messageTitle}
            </Headline>
          )}
        </S.MessageContainer>
      ) : null}

      <S.Form onSubmit={onSubmit}>
        {error && <S.ErrorMessage>{error.toString()}</S.ErrorMessage>}

        {inputsMapped}

        <S.Wrapper>
          {hasForgotPassword ? (
            <S.ForgotPassword
              type="button"
              onClick={() => navigate('forgot-password')}
              motive="inlineRed"
              scale="xs"
            >
              Forgot Password?
            </S.ForgotPassword>
          ) : null}

          <Button
            isLoading={isButtonLoading}
            textAlign="center"
            type="submit"
            motive="submitPrimary"
            scale="lg"
          >
            {buttonLabel}
          </Button>
        </S.Wrapper>
      </S.Form>

      {isVisible && <ConnectionStatus isVisibleSet={isVisibleSet} />}
    </S.Container>
  );
}
